/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useMemo} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, FormattedNumber} from 'react-intl'
import {Box, omitThemingProps, Stack, HStack, Text, useMultiStyleConfig} from '@chakra-ui/react'
import useBasket from '../../commerce-api/hooks/useBasket'
import {useItemVariant} from '.'
import {useCurrency} from '../../hooks'

import {getDisplayPrice} from '../../utils/product-utils'

const PricePerItem = ({currency, basket, basePrice}) => {
    const {currency: activeCurrency} = useCurrency()
    return (
        <Text fontSize={{base: '12px', lg: '14px'}}>
            <FormattedNumber
                style="currency"
                currency={currency || basket.currency || activeCurrency}
                value={basePrice}
            />
            <FormattedMessage
                defaultMessage="Price per item"
                id="price_per_item.label.each"
                description="Abbreviated 'each', follows price per item, like $10/ea"
            />
        </Text>
    )
}

PricePerItem.propTypes = {
    currency: PropTypes.string,
    basket: PropTypes.object,
    basePrice: PropTypes.string
}

/**
 * In the context of a cart product item variant, this component renders the item's
 * pricing, taking into account applied discounts as well as base item prices.
 */
const ItemPrice = (props) => {
    const variant = useItemVariant()
    const basket = useBasket()
    const {currency: activeCurrency} = useCurrency()
    const {currency, productLineItem, ...ownProps} = omitThemingProps(props)

    const {price, displayPrice, hasDiscount, bonusProductLineItem, discountPercentage} =
        useMemo(() => {
            const isProductASet = variant?.type?.set
            let price
            let displayPrice
            let hasDiscount
            let bonusProductLineItem = false
            if (isProductASet) {
                const psPrices = getDisplayPrice(variant)
                price = psPrices.price
                displayPrice = psPrices.displayPrice
                hasDiscount = psPrices.hasDiscount
            } else {
                const correspondingPLI =
                    productLineItem ||
                    basket?.productItems?.find((item) => item.productId === variant.id)
                price = correspondingPLI?.price || variant.price
                displayPrice =
                    correspondingPLI?.priceAfterItemDiscount ||
                    correspondingPLI?.priceAfterItemDiscount === 0
                        ? Math.min(price, correspondingPLI?.priceAfterItemDiscount)
                        : price
                hasDiscount = displayPrice !== price
                bonusProductLineItem = correspondingPLI?.bonusProductLineItem
            }

            // invalid rounding with floor. Here is an example:
            // Exactly 5% -> (100 * (239- 227.05)) / 239
            // result is 4.999999999999996 and after using
            // floor the result is 4%, but the round returns 5%
            const discountPercentage = Math.round((100 * (price - displayPrice)) / price)

            return {price, displayPrice, hasDiscount, bonusProductLineItem, discountPercentage}
        }, [basket, variant, productLineItem])

    const styles = useMultiStyleConfig('ItemVariantPrice', {...props, hasDiscount})

    return (
        <Stack {...styles.container} {...ownProps}>
            {bonusProductLineItem ? (
                <FormattedMessage
                    defaultMessage="Bonus Product"
                    id="item_attributes.label.bonus_product"
                />
            ) : (
                <>
                    <HStack>
                        <Box {...styles.priceContainer}>
                            <Text {...styles.price}>
                                <FormattedNumber
                                    style="currency"
                                    currency={currency || basket.currency || activeCurrency}
                                    value={displayPrice}
                                />
                            </Text>
                        </Box>
                        {hasDiscount && (
                            <Text {...styles.discount}>
                                <FormattedNumber
                                    style="currency"
                                    currency={currency || basket.currency || activeCurrency}
                                    value={price}
                                />
                            </Text>
                        )}
                    </HStack>
                    {hasDiscount && (
                        <Box pt={1} textStyle="Regular Small">
                            <FormattedMessage
                                defaultMessage="You save {discountPercentage}%"
                                values={{discountPercentage: discountPercentage}}
                                id="item_attributes.label.savings"
                            />
                        </Box>
                    )}
                </>
            )}
        </Stack>
    )
}

ItemPrice.propTypes = {
    productLineItem: PropTypes.object,
    currency: PropTypes.string,
    variant: PropTypes.oneOf(['cart', 'miniCart', 'pdp', 'order', 'tile'])
}

export default ItemPrice
